import PendingTaskShortcuts from 'components/organisms/pending-task-shortcuts'
import SalesDealsApi from 'api/admins/sales-deals-api'
import { useAuth } from 'providers/auth-provider'
import { TitlePage } from 'components/atoms'
import { PER_PAGE_OPTIONS, SCOPE, SORT_OPTIONS } from 'domains/outbound/deals/constants'
import { OutboundDealsPagingFilterField } from 'domains/outbound/deals/fields'
import { OutboundDealsFilterForm } from 'domains/outbound/deals/forms'
import { OutboundDealsUrlParamsTable } from 'domains/outbound/deals/url-params-table'
import { CREATED_DEAL_BY_FILTER_OPTIONS, PAYMENT_METHOD_FILTER_OPTIONS } from 'domains/sales/deals/constants/sales-deals-constants'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router'
import { Alert, Badge, Button, Card, Col, Row } from 'reactstrap'
import ParamsUtil from 'utils/params-util'

// ✅ NEW: same as Finance Deals List
const DEAL_STATE_OPTIONS = [
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'on_delivery', label: 'On Delivery' },
  { value: 'on_sources', label: 'On Sources' },
  { value: 'sourced', label: 'Sourced' },
  { value: 'waiting_for_confirmation', label: 'Waiting for Confirmation' },
  { value: 'waiting_for_payment', label: 'Waiting for Payment' },
  { value: 'waiting_for_shipment', label: 'Waiting for Shipment' },
]

const DEFAULT_FILTERS = {
  page                : 1,
  q                   : '',
  scope               : SCOPE.ALL,
  sort_by             : 'deals.created_at',
  sort_dir            : 'desc',
  created_by_admin_id : '',
  created_deal_by     : '',
  pickup_day          : '',
  payment_method      : '',
  deal_states         : '',
}

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const SalesDealsListPage = ({ pageUtils }) => {
  const history  = useHistory()
  const location = useLocation()

  const [ deals, setDeals ] = useState([])
  const [ pagination, setPagination ]          = useState({})
  const [ isLoading, setIsLoading ]            = useState(false)
  const [ createdByOptions, setCreatedByOptions ] = useState([])

  const filters = Object.fromEntries(new URLSearchParams(location.search))

  const initialPerPage          = filters?.per_page || PER_PAGE_OPTIONS[0].value
  const [ perPage, setPerPage ] = useState(initialPerPage)

  const initialSortBy  = filters?.sort_by  || 'deals.created_at'
  const initialSortDir  = filters?.sort_dir  || 'desc'

  const [ sortBy, setSortBy ]    = useState(initialSortBy)
  const [ sortDir , setSortDir ] = useState(initialSortDir)
  const pathName                 = Object.fromEntries(new URLSearchParams(location)).pathname

  const { user } = useAuth()

  const normalizedRole = user?.role?.toString().toLowerCase()
    const fullAccessDealEmails = [
    'jac@belleandkate.com',
    'angel@belleandkate.com',
  ]

  const normalizedEmail = user?.email?.toString().toLowerCase()
  const canShowAssistCutoffNotice = normalizedRole === 'sales'

  const canShowCreatedByFilter =
    normalizedRole === 'owner' ||
    normalizedRole === 'admin' ||
    fullAccessDealEmails.includes(normalizedEmail)

  useEffect(() => {
    handleFetchDeals()
  }, [ location.search ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    SalesDealsApi.getCreatedByOptions()
      .then((response) => {
        setCreatedByOptions(response?.data?.data || [])
      })
      .catch(() => {})
  }, [])

  const handleFetchDeals = () => {
    setIsLoading(true)

    let params
    const isEmptyParams = Object.keys(filters).length === 0

    if (isEmptyParams) {
      // ✅ Auto apply ALL + created_at desc on first load
      history.replace({
        pathname : pathName,
        search   : new URLSearchParams(DEFAULT_FILTERS).toString()
      })
      return
    } else {
      params = filters
    }

    SalesDealsApi.get({ ...params })
      .then((response) => {
        const rawDeals = response.data.data || []
        const normalizedDeals = rawDeals.map((d) => {
          const user = d?.user ? { ...d.user } : {}
          if (d?.user_detail) {
            user.detail = d.user_detail
          }

          return {
            ...d,
            user,
            payment: d?.payment
              ? {
                ...d.payment,
                created_at: d?.created_at || d.payment.created_at,
              }
              : d?.payment,
            created_deal_by: d?.sales_dashboard_created_deal_by_label || d?.created_deal_by || '-',
            sku_list: d?.sales_dashboard_product_detail_list || d?.sku_list,
            sku_list_with_qty: d?.sales_dashboard_product_detail_list || d?.sku_list_with_qty,
            sales_dashboard_product_detail_text: d?.sales_dashboard_product_detail_text || '-',
          }
        })
        setDeals(normalizedDeals)
        setPagination(response.data.meta)
      })
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally( () => setIsLoading(false) )
  }

  const handleSubmitFilter = (values, formik) => {
    //remove empty attributes from params
    let params = ParamsUtil.removeEmptyAttributes(values)
    //reset page
    params = ParamsUtil.removeAttributeFromObject('page', params)

    params = {
      ...params,
      per_page : perPage,
      sort_by  : sortBy,
      sort_dir : sortDir
    }

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })

    formik.setSubmitting(false)
  }

  const setPerPageToDefault = () => {
    setPerPage(PER_PAGE_OPTIONS[0].value)
  }

  const setSortToDefault = () => {
    setSortBy( SORT_OPTIONS[ 0 ].sort_by)
    setSortDir( SORT_OPTIONS[ 0 ].sort_dir)
  }

  const handleResetFilter = () => {
    setPerPageToDefault()

    setSortBy('deals.created_at')
    setSortDir('desc')

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(DEFAULT_FILTERS).toString()
    })
  }

  const handlePageChange = (page) => {
    const params = filters
    //replace page value
    params[ 'page' ] = page

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })
  }

  const handlePerPageOnChange = (event) => {
    const selectedIndex = event.nativeEvent.target.selectedIndex
    const selectedValue = event.nativeEvent.target[ selectedIndex ].value
    setPerPage(selectedValue)

    const params         = filters
    params[ 'per_page' ] = selectedValue
    history.push({
      search: new URLSearchParams(params).toString()
    })
  }

  const handleSortOnClick = (option) => {
    setSortBy(option.sort_by)
    setSortDir(option.sort_dir)

    const params         = filters
    params[ 'sort_by' ]  = option.sort_by
    params[ 'sort_dir' ] = option.sort_dir
    history.push({
      search: new URLSearchParams(params).toString()
    })
  }

  const handleClickRemoveFilter = (key) => {
    let params = filters
    //replace page value
    params[ key ] = ''
    if (key === 'per_page') {
      setPerPageToDefault()
    }

    if (key === 'sort_dir' || key === 'sort_by') {
      setSortToDefault()
      params['sort_by']  = ''
      params['sort_dir'] = ''
    }

    params = ParamsUtil.removeEmptyAttributes(params)

    history.push({
      pathname : pathName,
      search   : new URLSearchParams(params).toString()
    })
  }

  const handleClickRow = (dealId) => {
    const dealDetailPath = `/app/sales/deals/${dealId}`

    history.push({ pathname: dealDetailPath })
  }

  const hiddenColumns = [
    'deals.authentication_status_summary',
    'deals.paid_at',
    'payments.paid_at',
    'payment.paid_at',
    'deals.pickup_at',
    'user.email',
    'users.email',
    'deals.user.email',
    'deals.customer_email',
  ]

  return (
    <>
      <TitlePage mainTitle={ 'Sales Deals' } subTitle={ 'List' } />
      <PendingTaskShortcuts tasks={ ['sales_payment'] } />

      {canShowAssistCutoffNotice && (
        <Alert color='warning' className='mb-3'>
          <div className='font-weight-bold mb-1'>Monthly Cut Off Edit Assist By</div>
          <div>
            Deal yang dibuat sebelum tanggal 25 pukul 17.00 WIB hanya dapat mengubah Assist By sampai cut off tanggal 25 pukul 17.00 WIB pada bulan tersebut.
            Deal yang dibuat mulai tanggal 25 pukul 17.00 WIB masuk recap bulan berikutnya dan dapat mengubah Assist By sampai tanggal 25 pukul 17.00 WIB bulan berikutnya. Setelah cut off terlewati, Assist By tetap terkunci dan tidak dibuka kembali untuk role selain Owner/Admin. Owner/Admin tetap dapat mengubah Assist By setelah cut off.
          </div>
        </Alert>
      )}

      <Row>
        <Col
          className='col-xl-3'
          style={{
            height   : '80vh',
            overflow : 'scroll',
            position : 'sticky',
            top      : 0
          }}
        >
          <div>
            <OutboundDealsPagingFilterField
              perPage={ perPage }
              sortBy={ sortBy }
              sortDir={ sortDir }
              handlePerPageOnChange={ handlePerPageOnChange }
              handleSortOnClick={ handleSortOnClick }
            />
          </div>

          <div>
            <OutboundDealsFilterForm
              onClickSubmitForm={ handleSubmitFilter }
              filterValues={ filters }
              onClickReset={ handleResetFilter }
              canShowCreatedByFilter={ canShowCreatedByFilter }
              createdByOptions={ createdByOptions }
              enableCreatedDealByFilter={ true }
              createdDealByOptions={ CREATED_DEAL_BY_FILTER_OPTIONS }
              enableDealStateFilter={ true }
              dealStateOptions={ DEAL_STATE_OPTIONS }
              enablePaymentMethodFilter={ true }
              paymentMethodOptions={ PAYMENT_METHOD_FILTER_OPTIONS }
            />
          </div>
        </Col>

        <Col className='col-xl-9'>
          <Card>
            <div className='m-3'>
              <h5 className='text-muted text-uppercase'>{ 'Applied Filters' }</h5>
              {
                Object.keys(filters).length > 0
                  ? (
                    Object.keys(filters).map((key) => (
                      <Badge color='primary' key={ `outbound-deals-filters-${key}` }>
                        <div className='d-flex justify-content-between'>
                          <span className='align-self-center'>{ key }: { filters[ key ] }</span>
                          <Button close onClick={ () => handleClickRemoveFilter(key) } />
                        </div>
                      </Badge>
                    ))
                  )
                  : <Badge color='warning'>{ 'no filter applied' }</Badge>
              }
            </div>
          </Card>

          <Card>
            <OutboundDealsUrlParamsTable
              tableProps={{
                size       : 'sm',
                responsive : true,
                hover      : true
              }}
              deals={ deals }
              pagination={ pagination }
              isLoading={ isLoading }
              onPageChange={ handlePageChange }
              onClickRow={ handleClickRow }
              hiddenColumns={ hiddenColumns }
            />
          </Card>
        </Col>
      </Row>

    </>
  )
}

SalesDealsListPage.propTypes = propTypes

export default SalesDealsListPage
